/**
 * 动态视口高度管理器
 * 作为 100dvh/svh 的兜底方案，只在CSS方案不工作时启用
 * 显著改善但不能保证100%解决所有设备的所有情况
 */

import React from 'react';

// 检查浏览器是否支持现代视口单位
const supportsModernViewport = (): boolean => {
  return CSS.supports('height: 100dvh') || CSS.supports('height: 100svh');
};

class ViewportHeightManager {
  private static instance: ViewportHeightManager | null = null;
  private currentHeight: number = 0;
  private resizeTimeout: NodeJS.Timeout | null = null;
  private listeners: Set<(height: number) => void> = new Set();
  private isActive: boolean = false;

  private constructor() {
    this.updateHeight = this.updateHeight.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  public static getInstance(): ViewportHeightManager {
    if (!ViewportHeightManager.instance) {
      ViewportHeightManager.instance = new ViewportHeightManager();
    }
    return ViewportHeightManager.instance;
  }

  public init() {
    if (this.isActive) return;

    this.isActive = true;

    // 初始设置
    this.updateHeight();

    // 监听各种可能改变视口的事件
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('orientationchange', this.handleResize);

    // 移动端特殊处理：监听visual viewport变化
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.handleResize);
    }

    // 页面可见性变化时也更新（从后台回到前台）
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(this.updateHeight, 100);
      }
    });
  }

  private updateHeight() {
    // 使用window.innerHeight获取当前真实视口高度
    const newHeight = window.innerHeight;

    if (newHeight !== this.currentHeight) {
      this.currentHeight = newHeight;

      // 更新CSS变量
      document.documentElement.style.setProperty('--vh', `${newHeight * 0.01}px`);

      // 通知所有监听器
      this.listeners.forEach(listener => listener(newHeight));
    }
  }

  private handleResize() {
    // 防抖处理，避免频繁更新
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.updateHeight();
    }, 100);
  }

  public getCurrentHeight(): number {
    return this.currentHeight || window.innerHeight;
  }

  public addListener(callback: (height: number) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  public destroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('orientationchange', this.handleResize);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleResize);
    }

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.listeners.clear();
  }
}

// React Hook for using viewport height（仅在JS方案启用时工作）
export const useViewportHeight = () => {
  const [height, setHeight] = React.useState<number>(window.innerHeight);

  React.useEffect(() => {
    // 只有在JS方案启用时才监听变化
    if (!supportsModernViewport()) {
      const manager = ViewportHeightManager.getInstance();
      setHeight(manager.getCurrentHeight());

      const unsubscribe = manager.addListener(setHeight);
      return unsubscribe;
    }
  }, []);

  return height;
};

// 获取当前的vh值（用于CSS-in-JS）
export const getCurrentVH = (): string => {
  return ViewportHeightManager.getInstance().getCurrentHeight() * 0.01 + 'px';
};

// 初始化视口高度管理器（智能启用：只在CSS方案不支持时使用JS兜底）
export const initViewportHeight = (): void => {
  // 如果浏览器不支持现代视口单位，才启用JS方案
  if (!supportsModernViewport()) {
    console.log('浏览器不支持100dvh/svh，使用JS兜底方案');
    ViewportHeightManager.getInstance().init();
  } else {
    console.log('浏览器支持100dvh/svh，使用纯CSS方案');
  }
};
